import React, { FC, useCallback, useState } from 'react';
import { BasicContainer, FlexCol } from '@minecraft.atoms';
import { Elements } from '@recurly/react-recurly';
import { Form, Formik, FormikHelpers, FormikValues, useFormikContext } from 'formik';
import { useTranslation } from '@blocs.i18n';
import { captureExceptionEvent } from '@blocs.sentry';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { UseBilling } from '../../useBilling';
import { PaywallLocation } from '../../enums/PaywallLocation';
import { priceFormatter } from '../../helpers/priceFormatter';
import { generateUniqueId } from '../../helpers/functions';
import { AddPaymentProps, CheckoutProps, PaymentDetailFormProps } from '../types';
import { useAddPaymentMethod } from '../useAddPaymentMethods';
import { Purchase } from '../../types';
import { BillingCodes } from '../../enums/plans';
import { AddPaymentSection } from './AddPaymentSection';
import { PaymentSummarySection } from './PaymentSummarySection';

const RecurlyFormContent: FC<
  PaymentDetailFormProps & {
    showApplePayButton?: boolean;
    onClickApplePay: () => void;
    showPayPal?: boolean;
    onClickPayPal: () => void;
    formRef?: React.RefObject<HTMLFormElement>;
    selectedPaymentOption?: CheckoutProps['selectedPaymentOption'];
    threeDActionContext?: CheckoutProps['threeDActionContext'];
    acceptedTrialTerms: boolean;
    setAcceptedTrialTerms: (value: boolean) => void;
  }
> = ({
  addPaymentError,
  addPaymentLoading,
  showApplePayButton,
  onClickApplePay,
  showPayPal,
  onClickPayPal,
  formRef,
  selectedPlan,
  currentPurchase,
  currencyCode,
  countryCode,
  paywallLocation,
  selectedPaymentOption,
  allowCancel,
  completePurchaseLoading,
  completePurchaseError,
  acceptedTrialTerms,
  setAcceptedTrialTerms,
  onApplyCoupon,
  couponLoading,
  applyCouponError,
  onRemoveCoupon,
  threeDActionContext,
}) => {
  const uniqueId = generateUniqueId({ plan: selectedPlan, paywallLocation, customPrefix: 'complete-purchase-button' });
  const { values } = useFormikContext(); // Now useFormikContext can be used
  const { t } = useTranslation();

  const billingCode = selectedPlan?.billingCode ?? currentPurchase?.subscription?.plan?.billingCode;
  const isFreePlan = billingCode === BillingCodes.registration;

  const price = priceFormatter({
    price: currentPurchase?.total ?? 0,
    currencyCode,
    countryCode,
    t,
  });

  const getCompletePurchaseCta = (isTrialPlan: boolean, isPlanFree: boolean) => {
    if (isTrialPlan) return selectedPlan?.cta;
    if (isPlanFree) return t('common:paywall.summary.register');
    return t('common:paywall.summary.pay', { price });
  };

  const areAllRequiredFieldsFilled = isFreePlan
    ? acceptedTrialTerms
    : ['first_name', 'last_name', 'postal_code'].every((field) => Boolean(values[field]));

  return (
    <Form ref={formRef} className="cn_w-full">
      <FlexCol gap="6" maxW="21" w="full">
        {!isFreePlan && (
          <AddPaymentSection
            showApplePayButton={showApplePayButton}
            onClickApplePay={onClickApplePay}
            showPayPal={showPayPal}
            onClickPayPal={onClickPayPal}
          />
        )}
        <PaymentSummarySection
          addPaymentError={addPaymentError}
          addPaymentLoading={addPaymentLoading}
          completePurchaseLoading={completePurchaseLoading}
          selectedPlan={selectedPlan}
          currentPurchase={currentPurchase}
          currencyCode={currencyCode}
          countryCode={countryCode}
          selectedPaymentOption={selectedPaymentOption}
          completePurchaseError={completePurchaseError}
          uniqueId={uniqueId}
          acceptedTrialTerms={acceptedTrialTerms}
          setAcceptedTrialTerms={setAcceptedTrialTerms}
          areAllRequiredFieldsFilled={isFreePlan ? true : areAllRequiredFieldsFilled}
          getCompletePurchaseCta={getCompletePurchaseCta}
          allowCancel={allowCancel}
          onApplyCoupon={onApplyCoupon}
          couponLoading={couponLoading}
          applyCouponError={applyCouponError}
          onRemoveCoupon={onRemoveCoupon}
          threeDActionContext={threeDActionContext}
        />
      </FlexCol>
    </Form>
  );
};

const PaymentDetailForm: FC<
  PaymentDetailFormProps & {
    paywallLocation?: PaywallLocation;
    onAddPaymentOption: UseBilling['onAddPaymentOption'];
    onCompletePurchase: CheckoutProps['onCompletePurchase'];
    onPaymentCompleteRedirect: CheckoutProps['onPaymentCompleteRedirect'];
    handlePlanPurchase: (billingInfoId?: string) => Promise<void>;
    threeDActionContext?: CheckoutProps['threeDActionContext'];
  }
> = ({
  addPaymentError,
  addPaymentLoading,
  onAddPaymentOption,
  selectedPlan,
  currentPurchase,
  currencyCode,
  countryCode,
  paywallLocation,
  onCompletePurchase,
  allowCancel,
  selectedPaymentOption,
  onSelectPaymentOption,
  wallet,
  onNext,
  completedPurchase,
  completePurchaseLoading,
  completePurchaseError,
  onPaymentCompleteRedirect,
  handlePlanPurchase,
  onApplyCoupon,
  couponLoading,
  applyCouponError,
  onRemoveCoupon,
  threeDActionContext,
}) => {
  const [acceptedTrialTerms, setAcceptedTrialTerms] = useState<boolean>(false);
  const { showMessage } = useGlobalNotification();
  const { t } = useTranslation();
  const isFreePlanSelected = selectedPlan?.billingCode === BillingCodes.registration;

  const {
    requestRecurlyTokenAndValidate,
    showApplePayButton,
    onClickApplePay,
    onClickPayPal,
    showPayPal,
    formRef,
    formikRef,
  } = useAddPaymentMethod({
    onAddPaymentOption,
    currencyCode,
    countryCode,
    cb: (id) => handlePlanPurchase(id),
  });

  const validateFormAndSubmit = useCallback(
    async (formData: FormikValues, formikHelpers: FormikHelpers<FormikValues>) => {
      try {
        if (isFreePlanSelected) {
          await onCompletePurchase({ termsAccepted: true, isRedirectToOpenCall: isFreePlanSelected });
          onPaymentCompleteRedirect('login');

          showMessage({
            message: t('common:paywall.payment.success'),
            color: 'success',
          });
          return;
        }
        const errors = await formikHelpers.validateForm();
        if (Object.keys(errors).length > 0) {
          throw new Error(String(t('common:paywall.payment.error')));
        }
        requestRecurlyTokenAndValidate(formData, formikHelpers);
      } catch (_) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        showMessage({
          message: t('common:paywall.payment.error'),
          color: 'paywallError',
        });
      }
    },
    [isFreePlanSelected, onCompletePurchase, onPaymentCompleteRedirect, requestRecurlyTokenAndValidate, showMessage, t]
  );

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        // eslint-disable-next-line camelcase
        first_name: '',
        // eslint-disable-next-line camelcase
        last_name: '',
        // eslint-disable-next-line camelcase
        postal_code: '',
        paymentPriority: [],
      }}
      onSubmit={validateFormAndSubmit}
    >
      <RecurlyFormContent
        paywallLocation={paywallLocation}
        addPaymentError={addPaymentError}
        addPaymentLoading={addPaymentLoading}
        currencyCode={currencyCode}
        countryCode={countryCode}
        showApplePayButton={showApplePayButton}
        onClickApplePay={onClickApplePay}
        showPayPal={showPayPal}
        onClickPayPal={onClickPayPal}
        formRef={formRef}
        selectedPlan={selectedPlan}
        currentPurchase={currentPurchase}
        acceptedTrialTerms={acceptedTrialTerms}
        setAcceptedTrialTerms={setAcceptedTrialTerms}
        allowCancel={allowCancel}
        selectedPaymentOption={selectedPaymentOption}
        onSelectPaymentOption={onSelectPaymentOption}
        wallet={wallet}
        onNext={onNext}
        completedPurchase={completedPurchase}
        completePurchaseLoading={completePurchaseLoading}
        completePurchaseError={completePurchaseError}
        onApplyCoupon={onApplyCoupon}
        couponLoading={couponLoading}
        applyCouponError={applyCouponError}
        onRemoveCoupon={onRemoveCoupon}
        threeDActionContext={threeDActionContext}
      />
    </Formik>
  );
};

export const AddPaymentMethodV2: FC<AddPaymentProps> = ({
  addPaymentError,
  addPaymentLoading,
  allowCancel,
  onAddPaymentOption,
  threeDActionContext,
  currencyCode,
  countryCode,
  paywallLocation,
  onCompletePurchase,
  selectedPlan,
  currentPurchase,
  selectedPaymentOption,
  wallet,
  onSelectPaymentOption,
  onNext,
  completedPurchase,
  completePurchaseLoading,
  completePurchaseError,
  onPaymentCompleteRedirect,
  onApplyCoupon,
  couponLoading,
  applyCouponError,
  onRemoveCoupon,
}) => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();

  const isFreePlanSelected = selectedPlan?.billingCode === BillingCodes.registration;
  const handlePlanPurchase = async (billingInfoId?: string) => {
    try {
      if (billingInfoId) {
        onSelectPaymentOption({ id: billingInfoId });
        await onCompletePurchase({ termsAccepted: true, isRedirectToOpenCall: isFreePlanSelected, billingInfoId });
        onNext();
        showMessage({
          message: t('common:paywall.payment.success'),
          color: 'success',
        });
      }
    } catch (error) {
      // we do not want to display the error notification if the 3DS flow is required
      if (error?.graphQLErrors?.[0]?.data?.errorData?.threeDSecureActionTokenId) return;

      window.scrollTo({ top: 0, behavior: 'smooth' });
      showMessage({
        message: t('common:paywall.payment.error'),
        color: 'paywallError',
      });
      captureExceptionEvent({ location: 'Talent => Plan Complete Purchase', error });
    }
  };

  return (
    <BasicContainer w="full" padding={4} data-testid="add-payment-container">
      <FlexCol gap="4">
        <Elements>
          <PaymentDetailForm
            allowCancel={allowCancel}
            addPaymentError={addPaymentError}
            onAddPaymentOption={onAddPaymentOption}
            addPaymentLoading={addPaymentLoading}
            currencyCode={currencyCode}
            countryCode={countryCode}
            paywallLocation={paywallLocation}
            onCompletePurchase={onCompletePurchase}
            selectedPlan={selectedPlan}
            currentPurchase={currentPurchase as Purchase}
            completePurchaseError={completePurchaseError}
            selectedPaymentOption={selectedPaymentOption}
            wallet={wallet}
            onSelectPaymentOption={onSelectPaymentOption}
            onNext={onNext}
            completedPurchase={completedPurchase}
            completePurchaseLoading={completePurchaseLoading}
            onPaymentCompleteRedirect={onPaymentCompleteRedirect}
            handlePlanPurchase={handlePlanPurchase}
            onApplyCoupon={onApplyCoupon}
            couponLoading={couponLoading}
            applyCouponError={applyCouponError}
            onRemoveCoupon={onRemoveCoupon}
            threeDActionContext={threeDActionContext}
          />
        </Elements>
      </FlexCol>
    </BasicContainer>
  );
};
